import type { GlobalState } from '../global/types';

import { IS_MOCKED_CLIENT } from '../config';
import { loadCache, loadCachedSharedState } from '../global/cache';
import {
  getGlobal, setGlobal,
} from '../global/index';
import { INITIAL_GLOBAL_STATE } from '../global/initialState';
import { updatePasscodeSettings } from '../global/reducers';
import { cloneDeep } from './iteratees';
import { clearStoredSession } from './sessions';

export async function initGlobal(force: boolean = false, prevGlobal?: GlobalState) {
  prevGlobal = prevGlobal || getGlobal();
  if (!force && 'byTabId' in prevGlobal) {
    return;
  }

  const initial = cloneDeep(INITIAL_GLOBAL_STATE);
  const cache = await loadCache(initial);
  let global = cache || initial;
  if (IS_MOCKED_CLIENT) global.authState = 'authorizationStateReady';

  // Sync sharedState.settings to global.settings for backward compatibility
  // But preserve per-account settings like themes, messageTextSize
  if (global.sharedState && global.sharedState.settings) {
    const { themes, loadedWallpapers, privacy, messageTextSize, ...sharedSettings } = global.sharedState.settings as any;
    global.settings = {
      ...global.settings,
      byKey: {
        ...global.settings.byKey,
        ...sharedSettings,
      },
      performance: global.sharedState.settings.performance,
    };
  }

  const { hasPasscode, isScreenLocked } = global.passcode;
  if (hasPasscode && !isScreenLocked) {
    global = updatePasscodeSettings(global, {
      isScreenLocked: true,
    });

    clearStoredSession();
  }

  if (force) {
    global.byTabId = prevGlobal.byTabId;
  }

  if (!cache) { // Try loading shared state separately
    const storedSharedState = await loadCachedSharedState();
    if (storedSharedState) {
      global.sharedState = storedSharedState;
      // Sync sharedState.settings to global.settings for backward compatibility
      // But preserve per-account settings like themes, messageTextSize
      const { themes, loadedWallpapers, privacy, messageTextSize, ...sharedSettings } = storedSharedState.settings as any;
      global.settings = {
        ...global.settings,
        byKey: {
          ...global.settings.byKey,
          ...sharedSettings,
        },
        performance: storedSharedState.settings.performance,
      };
    }
  }

  setGlobal(global);
}
